import React, {useEffect, useCallback, useRef} from 'react'
import {createPortal} from 'react-dom'
import {
  Backdrop,
  Dialog,
  Header,
  TitleGroup,
  Title,
  Subtitle,
  CloseButton,
  Body,
  Footer,
  StatusBanner,
} from './Modal.styles'

// ─── Close icon ───────────────────────────────────────────────────────────────

const CloseIcon = () => (
  <svg
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M12 4L4 12M4 4l8 8"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
)

// ─── Modal ────────────────────────────────────────────────────────────────────
/**
 * Width resolution order:
 *   1. `width` — any valid CSS string: "800px", "75vw", "min(900px, 90%)"
 *   2. `size`  — named preset: 'sm' | 'md' | 'lg' | 'xl' | 'full'
 *   3. fallback — 'md' preset (560px)
 *
 * @param {object}  props
 *
 * Visibility
 * @param {boolean}         props.isOpen
 * @param {function}        props.onClose
 *
 * Content
 * @param {string}          [props.title]
 * @param {string}          [props.subtitle]
 * @param {React.ReactNode} props.children
 * @param {React.ReactNode} [props.footer]
 *
 * Layout & size
 * @param {'sm'|'md'|'lg'|'xl'|'full'} [props.size='md']   — named preset
 * @param {string}          [props.width]                    — arbitrary CSS, overrides size
 * @param {'center'|'top'|'bottom'} [props.align='center']
 * @param {'left'|'center'|'right'} [props.footerAlign='right']
 * @param {boolean}         [props.headerDivider=true]
 * @param {boolean}         [props.footerDivider=true]
 * @param {boolean}         [props.noPadding=false]
 *
 * Behaviour
 * @param {boolean}         [props.closeOnBackdrop=true]
 * @param {boolean}         [props.closeOnEsc=true]
 * @param {boolean}         [props.showCloseButton=true]
 * @param {boolean}         [props.lockScroll=true]
 *
 * Status banner
 * @param {'info'|'success'|'warning'|'danger'} [props.statusVariant]
 * @param {React.ReactNode} [props.statusMessage]
 *
 * Accessibility
 * @param {string}          [props.aria-label]
 * @param {string}          [props.aria-describedby]
 *
 * Portal
 * @param {Element}         [props.portalTarget]
 */

const Modal = ({
  isOpen,
  onClose,

  title,
  subtitle,
  children,
  footer,

  size = 'md',
  width, // ← arbitrary CSS width; overrides size when provided
  align = 'center',
  footerAlign = 'right',
  headerDivider = true,
  footerDivider = true,
  noPadding = false,

  closeOnBackdrop = true,
  closeOnEsc = true,
  showCloseButton = true,
  lockScroll = true,

  statusVariant,
  statusMessage,

  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedBy,

  portalTarget,
}) => {
  const dialogRef = useRef(null)

  // Lock body scroll
  useEffect(() => {
    if (!lockScroll) return
    if (isOpen) {
      const prev = document.body.style.overflow
      document.body.style.overflow = 'hidden'
      return () => {
        document.body.style.overflow = prev
      }
    }
  }, [isOpen, lockScroll])

  // Escape key
  const handleKeyDown = useCallback(
    e => {
      if (closeOnEsc && e.key === 'Escape') onClose?.()
    },
    [closeOnEsc, onClose],
  )

  useEffect(() => {
    if (!isOpen) return
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, handleKeyDown])

  // Focus trap
  useEffect(() => {
    if (isOpen && dialogRef.current) {
      const focusable = dialogRef.current.querySelector(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      )
      focusable?.focus()
    }
  }, [isOpen])

  // Backdrop click
  const handleBackdropClick = e => {
    if (closeOnBackdrop && e.target === e.currentTarget) onClose?.()
  }

  if (!isOpen) return null

  const hasHeader = title || showCloseButton
  const hasStatusBanner = statusVariant && statusMessage

  const modalContent = (
    <Backdrop align={align} onClick={handleBackdropClick} role="presentation">
      <Dialog
        ref={dialogRef}
        size={size}
        width={width} // ← forwarded to the styled component
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel ?? title}
        aria-describedby={ariaDescribedBy}
      >
        {hasHeader && (
          <Header divider={headerDivider}>
            {(title || subtitle) && (
              <TitleGroup>
                {title && <Title>{title}</Title>}
                {subtitle && <Subtitle>{subtitle}</Subtitle>}
              </TitleGroup>
            )}
            {showCloseButton && (
              <CloseButton
                onClick={onClose}
                aria-label="Close modal"
                type="button"
              >
                <CloseIcon />
              </CloseButton>
            )}
          </Header>
        )}

        {hasStatusBanner && (
          <StatusBanner variant={statusVariant}>{statusMessage}</StatusBanner>
        )}

        <Body noPadding={noPadding}>{children}</Body>

        {footer && (
          <Footer align={footerAlign} divider={footerDivider}>
            {footer}
          </Footer>
        )}
      </Dialog>
    </Backdrop>
  )

  return createPortal(modalContent, portalTarget ?? document.body)
}

export default Modal
